import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";
import { useDispatch, useSelector } from "react-redux";

import Dialog from "../../dialog/Dialog";
import { callAPIInterface } from "../../constants";
import List from "../List";
import CommonSkeleton from "../../CommonSkeleton";
import { useSession } from "../../../hooks/useSession";
import Text from "../../text/Text";
import { allLabel } from "../../messages";
import { setAppList } from "../../../redux/actions";

function CategoryDetails({ isOpen, categoryDetail, setIsOpen }) {
  const dispatch = useDispatch();

  const queryParam = "?limit=15";

  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState(queryParam);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(
    categoryDetail?.id
  );

  const { session } = useSession();

  const appList = useSelector((state) => state.common.appList);

  const listSkeleton = () => {
    return (
      <Box className="list-box">
        <Box display="flex" gap="10px">
          <CommonSkeleton
            variant="rectangular"
            height="56px"
            width="56px"
            borderRadius="14px"
          />
          <Box>
            <CommonSkeleton variant="text" />
            <CommonSkeleton variant="text" width="200px" />
          </Box>
        </Box>
        <CommonSkeleton
          variant="square"
          height="33px"
          width="70px"
          borderRadius="8px"
        />
      </Box>
    );
  };

  // Function to fetch filtered apps
  const fetchFilteredApps = async (lines, queryParams) => {
    const params = new URLSearchParams(queryParams);

    const payload = {
      restricted_countries: {
        not_contains: session?.account?.account?.country,
      },
      app_categories: { in: [selectedSubCategoryId] },
      limit: params.get("limit"),
      ending_before: params.get("ending_before"),
    };

    try {
      const response = await callAPIInterface(
        "POST",
        `/manage-app/apps/app-listing/filter`,
        JSON.stringify(payload)
      );
      if (!response?.has_more) {
        setHasMore(false);
      } else {
        setQueryParams(
          queryParam +
            "&ending_before=" +
            response.data[response.data.length - 1].id
        );
      }
      dispatch(setAppList(lines.concat(response?.data)));
    } catch (error) {
      console.error("Error fetching filtered apps:", error);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setAppList([]));
    };
  }, []);

  useEffect(() => {
    if (isOpen && session) {
      fetchFilteredApps([], queryParams);
    }
  }, [isOpen, session, selectedSubCategoryId]);

  const loadMore = useCallback(() => {
    if (hasMore) {
      fetchFilteredApps(appList, queryParams);
    }
  }, [appList, queryParams, hasMore]);

  const handleFilterCategory = (data) => {
    setSelectedSubCategoryId(data?.category_id);
    setHasMore(true);
    setQueryParams(queryParam);
    dispatch(setAppList([]));
  };

  return (
    <Dialog
      isOpenModal={isOpen}
      setIsOpenModal={() => setIsOpen(false)}
      modalHeader={categoryDetail?.name}
      directionToOpen={"right"}
      handleAction={() => setIsOpen(false)}
    >
      <>
        {!!categoryDetail?.subCategories.length && (
          <Box className="sub-category-wrapper">
            {[
              //default sub category should be 'All'
              {
                category_id: categoryDetail?.id,
                sub_category: allLabel,
              },
              ...(categoryDetail?.subCategories || []),
            ]?.map((data) => {
              return (
                <Box
                  key={data?.category_id}
                  className="sub-category"
                  onClick={() => handleFilterCategory(data)}
                  sx={{
                    border: `1px solid ${
                      data?.category_id === selectedSubCategoryId
                        ? "#2A67FF"
                        : "#F0F0F0"
                    }`,
                  }}
                >
                  <Text
                    className={
                      data?.category_id === selectedSubCategoryId
                        ? "blue-text"
                        : "default-text"
                    }
                    font="regular"
                    size={14}
                  >
                    {data.sub_category}
                  </Text>
                </Box>
              );
            })}
          </Box>
        )}
        <Box className="category-detail-wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={hasMore}
            loader={listSkeleton()}
            useWindow={false}
            className="transaction-list"
            style={{ width: "100%" }}
          >
            {appList?.map((data, idx) => {
              return (
                <List
                  data={{ ...data }}
                  idx={idx}
                  dataLength={appList?.length}
                  setIsDrawerOpen={setIsOpen}
                  key={data?.id}
                />
              );
            })}
          </InfiniteScroll>
        </Box>
      </>
    </Dialog>
  );
}

export default CategoryDetails;
